import { useSuspenseQuery } from '@tanstack/react-query'
import { useConfig } from 'wagmi'

import { SuspenseQueryWith } from '@/utils/types'

import { QUERY_STALE_TIME } from '@/config/consts'
import { gsmAbi, gsmAddress } from '@/config/contracts-generated'
import { BaseUnitNumber } from '@/domain/types/NumericValues'
import { queryOptions } from '@tanstack/react-query'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'
import { getContractAddress } from '@/domain/hooks/useContractAddress'

export interface UseGsmBalanceParams {
  chainId: number
  refetchInterval?: number
}

interface GsmBalanceResult {
  balance: BaseUnitNumber
}

export type UseGsmBalanceResult = SuspenseQueryWith<{
  data: GsmBalanceResult
}>

export function useGsmBalance({ chainId, refetchInterval }: UseGsmBalanceParams): UseGsmBalanceResult {
  const wagmiConfig = useConfig()

  const res = useSuspenseQuery({
    ...gsmBalanceQueryOptions({
      wagmiConfig,
      chainId,
    }),
    refetchInterval,
    staleTime: QUERY_STALE_TIME,
  })

  return {
    ...res,
    data: res.data,
  }
}

interface GsmBalanceParams {
  wagmiConfig: Config
  chainId: number
}

export function gsmBalanceQueryOptions({ wagmiConfig, chainId }: GsmBalanceParams) {
  return queryOptions<GsmBalanceResult>({
    queryKey: gsmBalanceQueryKey({ chainId }),
    queryFn: async () => {
      const gsmContractAddress = getContractAddress(gsmAddress, chainId)

      const balance = await readContract(wagmiConfig, {
        address: gsmContractAddress,
        abi: gsmAbi,
        functionName: 'getTotalUnderlying',
        chainId,
      })

      return { balance: BaseUnitNumber(balance) }
    },
  })
}

export function gsmBalanceQueryKey({ chainId }: Omit<GsmBalanceParams, 'wagmiConfig'>): unknown[] {
  return ['gsm-balance', chainId]
}
